import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import profileAssignmentService from '../services/profileAssignmentService';
import paymentporterService from '../services/paymentporterService';
import OhipProfilePicker from './OhipProfilePicker';
import './AddCorporateProfilePage.css';

const PROFILE_TYPES = [
    { value: 'COM',         label: 'Company' },
    { value: 'TravelAgent', label: 'Travel Agent' },
    { value: 'SRC',         label: 'Source' },
    { value: 'OTA',         label: 'OTA (Online Travel Agent)' },
];

const EMPTY_FORM = {
    profile_id:   '',
    profile_name: '',
    profile_type: 'COM',
    corporate_id: '',
    ar_account_no:   '',
    ar_account_name: '',
};

const AddProfileAssignmentPage = () => {
    const navigate                = useNavigate();
    const { ppId, assignmentId }  = useParams();
    const [searchParams]          = useSearchParams();
    const ruleId                  = searchParams.get('ruleId');
    const returnStep              = searchParams.get('returnStep');
    const isEdit                  = Boolean(assignmentId);
    const [form, setForm]         = useState(EMPTY_FORM);
    const [errors, setErrors]     = useState({});
    const [saving, setSaving]     = useState(false);
    // VCC or DB. A Direct Bill rule may only be assigned to a profile with an ACTIVE OPERA AR account, so its profile
    // is chosen through the AR account search, and the server re-checks the account against OPERA on save.
    const [ruleType, setRuleType] = useState(null);
    const isDb                    = ruleType === 'DB';

    useEffect(() => {
        if (isEdit) {
            profileAssignmentService.getById(ppId, assignmentId).then(res => {
                const a = res.data?.profileAssignment;
                if (a) {
                    setForm({
                        profile_id:      a.profile_id      || '',
                        profile_name:    a.profile_name    || '',
                        profile_type:    a.profile_type    || 'COM',
                        corporate_id:    a.corporate_id    || '',
                        ar_account_no:   a.ar_account_no   || '',
                        ar_account_name: a.ar_account_name || '',
                    });
                    setRuleType(a.rule_type || 'VCC');
                }
            }).catch(() => {});
        } else if (ruleId) {
            paymentporterService.getRule(ppId, ruleId)
                .then(res => setRuleType(res.data?.rule?.rule_type || 'VCC'))
                .catch(() => setRuleType('VCC'));
        }
    }, [ppId, assignmentId, isEdit, ruleId]);

    const handleArSelect = (account) => {
        setForm(f => ({
            ...f,
            profile_id:      account.profileId || '',
            profile_name:    account.name      || f.profile_name,
            ar_account_no:   account.accountNo || '',
            ar_account_name: account.accountName || '',
        }));
        setErrors({});
    };

    const set = (field, value) => {
        setForm(f => ({ ...f, [field]: value }));
        setErrors(e => ({ ...e, [field]: undefined }));
    };

    const handleOhipSelect = (profile) => {
        setForm(f => ({
            ...f,
            profile_id:   profile.profileId   || f.profile_id,
            profile_name: profile.name        || f.profile_name,
            profile_type: profile.profileType || f.profile_type,
            corporate_id: profile.corporateId || f.corporate_id,
        }));
        setErrors({});
    };

    const validate = () => {
        const e = {};
        if (!form.profile_id.trim()) e.profile_id = 'Profile ID is required';
        if (isDb && !form.ar_account_no) e.ar_account_no = 'Choose a profile with an active AR account';
        return e;
    };

    const handleSave = async () => {
        const e = validate();
        if (Object.keys(e).length) { setErrors(e); return; }
        setSaving(true);
        try {
            const payload = {
                profile_id:   form.profile_id.trim(),
                profile_name: form.profile_name.trim() || null,
                profile_type: form.profile_type || null,
                corporate_id: form.corporate_id.trim() || null,
                ...(isDb ? { ar_account_no: form.ar_account_no } : {}),
            };
            if (isEdit) {
                await profileAssignmentService.update(ppId, assignmentId, payload);
            } else {
                await profileAssignmentService.create(ppId, payload, ruleId);
            }
            navigate(ruleId ? `/apps/payment-porter/${ppId}/rules/${ruleId}${returnStep ? `?step=${returnStep}` : ''}` : `/apps/payment-porter/${ppId}`);
        } catch (err) {
            setErrors({ submit: err.response?.data?.error?.message || 'Failed to save profile assignment' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="acp-page">
            <div className="acp-card">
                <div className="acp-card-header">
                    <h1 className="acp-title">{isEdit ? 'Edit' : 'Assign'} OHIP Profile</h1>
                    <p className="acp-subtitle">
                        {isDb
                            ? 'Direct Bill: choose the company whose AR account qualifying reservations are billed to. Only profiles with an active AR account can be assigned.'
                            : 'Assign an OHIP profile to this Payment Porter to automatically apply it to qualifying reservations'}
                    </p>
                </div>

                <div className="acp-divider" />

                <div className="acp-body">
                    {/* OHIP search — AR accounts for a Direct Bill rule, profiles otherwise */}
                    {isDb ? (
                        <div className="acp-field">
                            <label className="acp-label">AR Account <span className="acp-required">*</span></label>
                            <OhipProfilePicker ppId={ppId} arAccounts onSelect={handleArSelect} buttonLabel="Search AR Accounts" />
                            {form.ar_account_no
                                ? <span className="acp-hint">AR account <strong>{form.ar_account_no}</strong>{form.ar_account_name ? ` — ${form.ar_account_name}` : ''}. Checked against OPERA when saved.</span>
                                : <span className="acp-hint">Search OPERA's AR accounts by name. The profile is taken from the account you choose.</span>}
                            {errors.ar_account_no && <span className="acp-error-msg">{errors.ar_account_no}</span>}
                        </div>
                    ) : (
                        <div className="acp-field">
                            <label className="acp-label">Search OHIP</label>
                            <OhipProfilePicker ppId={ppId} onSelect={handleOhipSelect} buttonLabel="Search OHIP Profiles" />
                            <span className="acp-hint">Search OHIP to auto-fill the fields below, or enter them manually.</span>
                        </div>
                    )}

                    {/* Profile ID + Type */}
                    <div className="acp-row-2">
                        <div className="acp-field">
                            <label className="acp-label">Profile ID <span className="acp-required">*</span></label>
                            <input
                                className={`acp-input${errors.profile_id ? ' acp-input-error' : ''}`}
                                type="text"
                                placeholder="e.g., 12345"
                                value={form.profile_id}
                                onChange={e => set('profile_id', e.target.value)}
                                readOnly={isDb}
                                title={isDb ? 'Taken from the AR account' : undefined}
                            />
                            {errors.profile_id && <span className="acp-error-msg">{errors.profile_id}</span>}
                        </div>
                        <div className="acp-field">
                            <label className="acp-label">Profile Type</label>
                            <select
                                className="acp-select"
                                value={form.profile_type}
                                onChange={e => set('profile_type', e.target.value)}
                            >
                                {PROFILE_TYPES.map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                                {!PROFILE_TYPES.find(t => t.value === form.profile_type) && form.profile_type && (
                                    <option value={form.profile_type}>{form.profile_type}</option>
                                )}
                            </select>
                        </div>
                    </div>

                    {/* Profile Name */}
                    <div className="acp-field">
                        <label className="acp-label">Profile Name</label>
                        <input
                            className="acp-input"
                            type="text"
                            placeholder="e.g., Booking.com"
                            value={form.profile_name}
                            onChange={e => set('profile_name', e.target.value)}
                        />
                    </div>

                    {/* Corporate ID */}
                    <div className="acp-field">
                        <label className="acp-label">Corporate ID</label>
                        <input
                            className="acp-input"
                            type="text"
                            placeholder="OHIP corporate ID (if applicable)"
                            value={form.corporate_id}
                            onChange={e => set('corporate_id', e.target.value)}
                        />
                    </div>

                    {errors.submit && (
                        <div className="acp-error-banner">{errors.submit}</div>
                    )}
                </div>

                <div className="acp-divider" />

                <div className="acp-footer">
                    <button
                        className="acp-btn-cancel"
                        onClick={() => navigate(ruleId ? `/apps/payment-porter/${ppId}/rules/${ruleId}${returnStep ? `?step=${returnStep}` : ''}` : `/apps/payment-porter/${ppId}`)}
                        disabled={saving}
                    >
                        Cancel
                    </button>
                    <button
                        className="acp-btn-save"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Assign Profile'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddProfileAssignmentPage;
